import PropTypes from 'prop-types';

const MapObject = {
    id: PropTypes.string.isRequired,
    lastModified: PropTypes.string,
};

class MapPropTypes {
    static AccountProps = PropTypes.shape({
        ...MapObject,
        active: PropTypes.bool.isRequired,
        billTo: PropTypes.string,
        bsdFlow: PropTypes.string,
        businessModel: PropTypes.string.isRequired,
        contact: PropTypes.string.isRequired,
        mentorId: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        payer: PropTypes.string,
        shipTo: PropTypes.string,
        soldTo: PropTypes.string,
    });

    static AccountRepProps = PropTypes.shape({
        ...MapObject,
        active: PropTypes.bool.isRequired,
        email: PropTypes.string.isRequired,
        firstName: PropTypes.string.isRequired,
        lastName: PropTypes.string.isRequired,
        mentorId: PropTypes.string.isRequired,
        phone: PropTypes.string.isRequired,
    });

    static ProductProps = PropTypes.shape({
        ...MapObject,
        active: PropTypes.bool.isRequired,
        factoryReferencePrice: PropTypes.number,
        factoryReferencePriceLocal: PropTypes.number,
        mentorId: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        productFamilyId: PropTypes.string.isRequired,
        productFamilyName: PropTypes.string.isRequired,
    });

    static ServerProps = PropTypes.shape({
        ...MapObject,
        active: PropTypes.bool.isRequired,
        comment: PropTypes.string,
        licenseType: PropTypes.string,
        mentorId: PropTypes.string.isRequired,
        platform: MapPropTypes.PlatformProps,
        productFlowCode: PropTypes.string,
        siteServerConfigId: PropTypes.string,
    });

    static SiteProps = PropTypes.shape({
        ...MapObject,
        account: PropTypes.oneOfType([
            PropTypes.string,
            MapPropTypes.AccountProps,
        ]).isRequired,
        active: PropTypes.bool.isRequired,
        address1: PropTypes.string,
        address2: PropTypes.string,
        city: PropTypes.string,
        contact1: PropTypes.string,
        contact2: PropTypes.string,
        country: PropTypes.string,
        mentorId: PropTypes.string.isRequired,
        name: PropTypes.string,
        phone: PropTypes.string,
        postalCode: PropTypes.string,
        servers: PropTypes.arrayOf(
            PropTypes.oneOfType([PropTypes.string, MapPropTypes.ServerProps])
        ).isRequired,
        state: PropTypes.string,
    });

    static PlatformProps = PropTypes.shape({
        ...MapObject,
        active: PropTypes.bool.isRequired,
        mentorId: PropTypes.string.isRequired,
        variant: PropTypes.string.isRequired,
        vendor: PropTypes.string.isRequired,
    });

    static LicenseProps = PropTypes.shape({
        ...MapObject,
        amount: PropTypes.number.isRequired,
        duration: PropTypes.number.isRequired,
        seats: PropTypes.number.isRequired,
        validFrom: PropTypes.string.isRequired,
        validTo: PropTypes.string.isRequired,
        product: MapPropTypes.ProductProps,
        site: MapPropTypes.SiteProps.isRequired,
        server: MapPropTypes.ServerProps,
        timezoneOffset: PropTypes.number.isRequired,
    });

    static PriceQuoteProps = PropTypes.shape({
        amount: PropTypes.number.isRequired,
        price: PropTypes.number.isRequired,
    });

    static PricingProps = PropTypes.shape({
        product: MapPropTypes.ProductProps.isRequired,
        weekly: PropTypes.arrayOf(MapPropTypes.PriceQuoteProps).isRequired,
        monthly: PropTypes.arrayOf(MapPropTypes.PriceQuoteProps).isRequired,
    });

    static RoleProps = PropTypes.shape({
        active: PropTypes.bool.isRequired,
        name: PropTypes.string.isRequired,
        description: PropTypes.string,
    });

    static UserProps = PropTypes.shape({
        ...MapObject,
        account: MapPropTypes.AccountProps,
        active: PropTypes.bool.isRequired,
        address1: PropTypes.string,
        address2: PropTypes.string,
        city: PropTypes.string,
        comment: PropTypes.string,
        country: PropTypes.string,
        email: PropTypes.string,
        firstName: PropTypes.string.isRequired,
        lastName: PropTypes.string.isRequired,
        phone: PropTypes.string,
        postalCode: PropTypes.string,
        roles: PropTypes.arrayOf(MapPropTypes.RoleProps).isRequired,
        signinOnly: PropTypes.bool.isRequired,
        signinTransition: PropTypes.string,
        state: PropTypes.string,
        username: PropTypes.string.isRequired,
        checkProductConfig: PropTypes.bool,
        checkHostServerConfig: PropTypes.bool
    });

    static TransactionProps = PropTypes.shape({
        ...MapObject,
        amount: PropTypes.number.isRequired,
        batchTimestamp: PropTypes.string,
        confirmation: PropTypes.string.isRequired,
        currency: PropTypes.string,
        dealConsumptionId: PropTypes.number,
        description: PropTypes.string,
        instructions: PropTypes.string,
        licenses: PropTypes.arrayOf(MapPropTypes.LicenseProps),
        offer: PropTypes.string.isRequired,
        type: PropTypes.string,
        user: PropTypes.oneOfType([
            PropTypes.string,
            MapPropTypes.UserProps,
            PropTypes.instanceOf(null),
        ]),
    });

    static OfferProps = PropTypes.shape({
        ...MapObject,
        account: MapPropTypes.AccountProps.isRequired,
        accountReps: PropTypes.arrayOf(MapPropTypes.AccountRepProps).isRequired,
        active: PropTypes.bool.isRequired,
        amount: PropTypes.number.isRequired,
        approved: PropTypes.string,
        balance: PropTypes.number.isRequired,
        currency: PropTypes.string,
        discount: PropTypes.number.isRequired,
        exportFormat: PropTypes.string.isRequired,
        fulfillmentContactId: PropTypes.string.isRequired,
        fulfillmentContactName: PropTypes.string.isRequired,
        idealDealId: PropTypes.string,
        isEld: PropTypes.bool.isRequired,
        mentorId: PropTypes.string.isRequired,
        po: PropTypes.string.isRequired,
        poDate: PropTypes.string.isRequired,
        poolId: PropTypes.string,
        pricing: PropTypes.arrayOf(MapPropTypes.PricingProps).isRequired,
        quote: PropTypes.string,
        sites: PropTypes.arrayOf(MapPropTypes.SiteProps).isRequired,
        transactions: PropTypes.arrayOf(MapPropTypes.TransactionProps)
            .isRequired,
        // users: PropTypes.arrayOf(MapPropTypes.UserProps).isRequired,
        validFrom: PropTypes.string.isRequired,
        validTo: PropTypes.string.isRequired,
    });

    static FaqProps = PropTypes.shape({
        answer: PropTypes.string.isRequired,
        question: PropTypes.string.isRequired,
    });

    static ContactProps = PropTypes.shape({
        kind: PropTypes.string.isRequired,
        recipients: PropTypes.arrayOf(PropTypes.string).isRequired,
    });

    static BsdFlowProps = PropTypes.arrayOf(PropTypes.string);

    static BusinessModelProps = PropTypes.arrayOf(PropTypes.string);

    static CountryProps = PropTypes.shape({
        abbr: PropTypes.string.isRequired,
        full: PropTypes.string.isRequired,
    });

    static CartItemProps = PropTypes.shape({
        monthCount: PropTypes.number.isRequired,
        productIndex: PropTypes.number.isRequired,
        seats: PropTypes.oneOfType([
            PropTypes.number.isRequired,
            PropTypes.string.isRequired,
        ]),
        serverIndex: PropTypes.number,
        siteIndex: PropTypes.number,
        weekCount: PropTypes.number.isRequired,
        saveForLater: PropTypes.bool.isRequired,
        itemDataChanged: PropTypes.bool.isRequired
    });

    static CartProps = PropTypes.arrayOf(MapPropTypes.CartItemProps);
}

export default MapPropTypes;



// WEBPACK FOOTER //
// ./app/components/MapPropTypes.js